/**
 * OnboardingCallBanner — appears only for yearly subscribers who still
 * have their free 1h onboarding call with Sergio to redeem.
 * States:
 *   - eligible + available   → "Prenota ora" CTA → opens Calendly in a new tab
 *   - eligible + booked      → shows scheduled date/time + manage link
 *   - eligible + completed   → not rendered (server returns status completed)
 *   - not eligible           → not rendered
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Gift, ArrowRight, CheckCircle, Calendar, Clock } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDT = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('it-IT', {
      weekday: 'long', day: 'numeric', month: 'long',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

export default function OnboardingCallBanner() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStatus = async () => {
    try {
      const r = await axios.get(`${API}/onboarding-call/status`, { withCredentials: true });
      setData(r.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    // refresh when user comes back to tab (they just booked → webhook may have fired)
    const onFocus = () => fetchStatus();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  if (loading || !data) return null;
  if (!data.eligible || data.status === 'completed') return null;

  const isBooked = data.status === 'booked';
  const ev = data.event || {};

  if (isBooked) {
    return (
      <div className="mb-5 sm:mb-7 relative overflow-hidden" data-testid="onboarding-banner-booked">
        <div className="bg-gradient-to-r from-emerald-600 via-emerald-700 to-teal-700 text-white p-5 sm:p-6 relative">
          <div className="absolute -right-16 -top-16 w-48 h-48 rounded-full bg-emerald-400/20 blur-3xl pointer-events-none" />
          <div className="relative flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="w-11 h-11 bg-white/15 flex items-center justify-center flex-shrink-0">
              <CheckCircle size={22} weight="fill" className="text-emerald-200" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-black uppercase tracking-[0.22em] text-emerald-200 mb-1">
                Ora di onboarding confermata
              </p>
              <p className="text-sm sm:text-base font-black leading-tight mb-0.5">
                La tua call con Sergio è prenotata
              </p>
              <p className="text-xs sm:text-sm text-emerald-100 flex items-center gap-1.5">
                <Calendar size={12} weight="bold" /> {fmtDT(ev.start_time)}
              </p>
            </div>
            <a
              href={ev.invitee_uri ? ev.invitee_uri : 'https://calendly.com/vitadahost/locazioneturistica'}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 border border-white/30 text-white text-[10px] sm:text-xs font-black uppercase tracking-wider px-4 py-2.5 transition-colors flex-shrink-0"
              data-testid="onboarding-manage-btn"
            >
              Gestisci prenotazione
            </a>
          </div>
        </div>
      </div>
    );
  }

  // status === 'available'
  return (
    <div className="mb-5 sm:mb-7 relative overflow-hidden" data-testid="onboarding-banner-available">
      <div className="bg-gradient-to-r from-fuchsia-600 via-purple-700 to-indigo-700 text-white p-5 sm:p-6 relative">
        <div className="absolute -left-16 -top-12 w-48 h-48 rounded-full bg-fuchsia-400/20 blur-3xl pointer-events-none" />
        <div className="absolute -right-16 -bottom-12 w-56 h-56 rounded-full bg-indigo-400/20 blur-3xl pointer-events-none" />
        <div className="relative flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="w-11 h-11 bg-white/15 flex items-center justify-center flex-shrink-0">
            <Gift size={22} weight="fill" className="text-yellow-300" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-black uppercase tracking-[0.22em] text-fuchsia-200 mb-1">
              Bonus piano annuale · Regalo
            </p>
            <p className="text-sm sm:text-base lg:text-lg font-black leading-tight mb-0.5 tracking-tight">
              1 ora gratis con Sergio ti aspetta
            </p>
            <p className="text-xs sm:text-sm text-fuchsia-100 flex items-center gap-1.5 flex-wrap">
              <Clock size={12} weight="bold" className="text-yellow-300" />
              Ti spiega tutti i trucchi e come sfruttare al meglio la piattaforma
            </p>
          </div>
          <a
            href={data.scheduling_url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setTimeout(fetchStatus, 30000)}
            className="inline-flex items-center justify-center gap-2 bg-yellow-400 hover:bg-yellow-300 text-zinc-900 text-xs sm:text-sm font-black uppercase tracking-wider px-5 py-3 sm:py-3.5 transition-all hover:gap-3 flex-shrink-0"
            data-testid="onboarding-book-btn"
          >
            Prenota ora <ArrowRight size={14} weight="bold" />
          </a>
        </div>
      </div>
    </div>
  );
}
